import {
  Attribute,
  Component, inject,
  Injector,
  Optional, ViewChild
} from '@angular/core';
import {CommonModule, DatePipe} from "@angular/common";

import {
  DISPLAY_TYPE,
  IWidgetConfig,
  provideAsWidgetRef,
  Parent,
  register,
  styler
} from "@wm/components-base";
import {BsDropdownModule} from "ngx-bootstrap/dropdown";
import moment from 'moment';
import {getFormattedDate, getNativeDateObject, runtimeEventManager} from "@wm/core";
import {BaseDateTimeComponent} from "../base-date-time.component";
import { getAppDefault } from '../utils/appDefaults';
import { isString } from "lodash-es";

const CURRENT_TIME = 'CURRENT_TIME';

const DEFAULT_CLS = 'input-group app-timeinput';
const WIDGET_CONFIG: IWidgetConfig = {
  widgetType: 'wm-time',
  hostClass: DEFAULT_CLS,
  displayType: DISPLAY_TYPE.INLINE_BLOCK
};

@Component({
  selector: '[wmTime]',
  template: `

      <input class="form-control app-textbox app-timeinput display-input" [attr.aria-label]="hint || 'Time field'"
             [name]="name"
             type="{{loadNativeDateInput ? 'time': 'text'}}"
             role="timer"
             [value]="displayValue"
             [disabled]="disabled || readonly"
             [required]="required"
             [readOnly]="isReadOnly">

      <span class="input-group-btn dropdown-toggle" *ngIf="!loadNativeDateInput">
            <button type="button" class="btn btn-default btn-date" [tabindex]="tabindex"
                    [disabled]="disabled || readonly || isCurrentTime">
              <i aria-hidden="true" class="app-icon wm-sl-l sl-time"></i></button>
        </span>
    `,
  providers: [
    provideAsWidgetRef(TimeComponent),
    DatePipe
  ],
  exportAs: 'wmTime',
  standalone: true,
  imports: [CommonModule, BsDropdownModule],
  styles: []
})
export class TimeComponent extends BaseDateTimeComponent {
  static initializeProps = register('wm-time');

  public isCurrentTime: boolean;
  public status = {isopen: false}
  public hint: any;
  private bsTimeValue: Date | undefined;
  private minTime: Date;
  private timeinterval: any;
  private maxTime: Date;

  constructor(inj: Injector,
              @Optional() viewParent: Parent, @Attribute('timepattern') public _timepattern: string) {
    super(inj, WIDGET_CONFIG, viewParent);
    this.datePipe = inject(DatePipe);
    styler(this.nativeElement, this);
  }

  override setAppDefaultForProp() {
    if (this._timepattern) {
      return;
    }
    let timepattern = getAppDefault('timeFormat');
    if (!timepattern) {
      runtimeEventManager.on('wms:app-defaults-ready').subscribe(() => {
        this.timepattern = getAppDefault('timeFormat');
      });
      return;
    }
    this.timepattern = timepattern;
  }

  get displayValue() {
    return getFormattedDate(this.datePipe, this.bsTimeValue, this.timepattern) || '';
  }

  protected override onPropertyChange(key: string, nv: any, ov?: any) {
    if (key === 'tabindex') {
      return;
    } else if (key === 'timepattern') {
      this.updateFormat('timepattern');
    } else if (key === 'datavalue') {
      if (nv) {
        if (isString(nv) && nv.startsWith('bind:')) {
          return;
        }
        if (nv === CURRENT_TIME) {
          this.isCurrentTime = true;

          this.setTimeInterval();
        } else {
          this.clearTimeInterval();

          this.bsTimeValue = getNativeDateObject(nv, {
            pattern: this.loadNativeDateInput ? this.outputformat : undefined,
            isNativePicker: this.loadNativeDateInput
          });
          this.isCurrentTime = false;
        }
      } else {
        this.bsTimeValue = undefined;
        this.clearTimeInterval();
        this.isCurrentTime = false;
      }
    } else {
      super.onPropertyChange(key, nv, ov);
    }
  }

  private clearTimeInterval() {
    if (this.timeinterval) {
      clearInterval(this.timeinterval);
      this.timeinterval = null;
    }
  }

  private setTimeInterval() {
    if (this.timeinterval) {
      return;
    }
    this.timeinterval = setInterval(() => {
      const now = new Date();
      now.setSeconds(now.getSeconds() + 1);
      this.onTimeChange(now);
    }, 1000);
  }

  private onTimeChange(newVal: any, isNativePicker?: boolean) {
    // For nativePicker, newVal is event, get the dateobject from the event.
    if (isNativePicker) {
      newVal = getNativeDateObject(newVal.target.value, {
        pattern: this.loadNativeDateInput ? this.outputformat : undefined,
        isNativePicker: this.loadNativeDateInput
      });
    }
    if (newVal) {
      this.bsTimeValue = newVal;
      // if the newVal is valid but not in the given range then highlight the input field
      this.timeNotInRange = this.minTime && this.maxTime && (newVal < this.minTime || newVal > this.maxTime);
    }
  }
}
